// Helper function to get quarter from month
export function getQuarter(month: number): number {
  return Math.floor((month - 1) / 3) + 1;
}

// Helper function to get month range for a quarter
export function getQuarterMonthRange(quarter: number): { startMonth: string; endMonth: string } {
  const monthNames = [
    'Jan',
    'Feb',
    'Mar',
    'Apr',
    'May',
    'Jun',
    'Jul',
    'Aug',
    'Sep',
    'Oct',
    'Nov',
    'Dec'
  ];

  const startMonthIndex = (quarter - 1) * 3;
  const endMonthIndex = startMonthIndex + 2;

  return {
    startMonth: monthNames[startMonthIndex],
    endMonth: monthNames[endMonthIndex]
  };
}

// Helper function to format quarter label with month range
export function formatQuarterLabel(quarter: number, year: number): string {
  const { startMonth, endMonth } = getQuarterMonthRange(quarter);
  const shortYear = year.toString().slice(-2);
  return `${startMonth} - ${endMonth} '${shortYear}`;
}

// Helper function to format monthly label
export function formatMonthlyLabel(month: number, year: number): string {
  const monthNames = [
    'Jan',
    'Feb',
    'Mar',
    'Apr',
    'May',
    'Jun',
    'Jul',
    'Aug',
    'Sep',
    'Oct',
    'Nov',
    'Dec'
  ];
  const monthName = monthNames[month - 1];
  const shortYear = year.toString().slice(-2);
  return `${monthName} '${shortYear}`;
}

// Helper function to get last N quarters
export function getLastNQuarters(n: number): { year: number; quarter: number }[] {
  const result = [];
  const now = new Date();
  let year = now.getFullYear();
  let quarter = getQuarter(now.getMonth() + 1);
  for (let i = 0; i < n; i++) {
    result.push({ year, quarter });
    quarter--;
    if (quarter === 0) {
      quarter = 4;
      year--;
    }
  }
  // Sort chronologically (oldest first)
  return result.reverse();
}

// Helper function to sort labels chronologically
export const sortLabelsChronologically = (labels: string[]): string[] => {
  return labels.sort((a, b) => {
    // Handle quarterly labels with short years (e.g., "Oct - Dec '25")
    if (a.includes(' - ') && b.includes(' - ') && a.includes("'") && b.includes("'")) {
      const yearA = parseInt(a.split("'")[1] || '0');
      const yearB = parseInt(b.split("'")[1] || '0');
      if (yearA !== yearB) return yearA - yearB;

      // Extract quarter from month range
      const getQuarterFromLabel = (label: string): number => {
        const startMonth = label.split(' - ')[0];
        const monthNames = [
          'Jan',
          'Feb',
          'Mar',
          'Apr',
          'May',
          'Jun',
          'Jul',
          'Aug',
          'Sep',
          'Oct',
          'Nov',
          'Dec'
        ];
        const monthIndex = monthNames.indexOf(startMonth);
        return Math.floor(monthIndex / 3) + 1;
      };

      return getQuarterFromLabel(a) - getQuarterFromLabel(b);
    }

    // Handle monthly labels with short years (e.g., "Oct '24")
    if (a.includes("'") && b.includes("'") && !a.includes(' - ') && !b.includes(' - ')) {
      const yearA = parseInt(a.split("'")[1] || '0');
      const yearB = parseInt(b.split("'")[1] || '0');
      if (yearA !== yearB) return yearA - yearB;

      // Extract month from label
      const getMonthFromLabel = (label: string): number => {
        const monthName = label.split("'")[0].trim();
        const monthNames = [
          'Jan',
          'Feb',
          'Mar',
          'Apr',
          'May',
          'Jun',
          'Jul',
          'Aug',
          'Sep',
          'Oct',
          'Nov',
          'Dec'
        ];
        return monthNames.indexOf(monthName) + 1;
      };

      return getMonthFromLabel(a) - getMonthFromLabel(b);
    }

    // Handle quarterly labels without years (e.g., "Oct - Dec")
    if (
      a.includes(' - ') &&
      b.includes(' - ') &&
      !a.includes('202') &&
      !b.includes('202') &&
      !a.includes("'") &&
      !b.includes("'")
    ) {
      // Extract quarter from month range
      const getQuarterFromLabel = (label: string): number => {
        const startMonth = label.split(' - ')[0];
        const monthNames = [
          'Jan',
          'Feb',
          'Mar',
          'Apr',
          'May',
          'Jun',
          'Jul',
          'Aug',
          'Sep',
          'Oct',
          'Nov',
          'Dec'
        ];
        const monthIndex = monthNames.indexOf(startMonth);
        return Math.floor(monthIndex / 3) + 1;
      };

      return getQuarterFromLabel(a) - getQuarterFromLabel(b);
    }

    // Handle quarterly labels with years (legacy format: "Oct - Dec 2024")
    if (a.includes(' - ') && b.includes(' - ') && a.includes('202') && b.includes('202')) {
      const yearA = parseInt(a.split(' ').pop() || '0');
      const yearB = parseInt(b.split(' ').pop() || '0');
      if (yearA !== yearB) return yearA - yearB;

      // Extract quarter from month range
      const getQuarterFromLabel = (label: string): number => {
        const startMonth = label.split(' - ')[0];
        const monthNames = [
          'Jan',
          'Feb',
          'Mar',
          'Apr',
          'May',
          'Jun',
          'Jul',
          'Aug',
          'Sep',
          'Oct',
          'Nov',
          'Dec'
        ];
        const monthIndex = monthNames.indexOf(startMonth);
        return Math.floor(monthIndex / 3) + 1;
      };

      return getQuarterFromLabel(a) - getQuarterFromLabel(b);
    }

    // Handle monthly labels (legacy format: "2025-06")
    if (
      a.includes('-') &&
      b.includes('-') &&
      !a.includes(' - ') &&
      !b.includes(' - ') &&
      !a.includes("'") &&
      !b.includes("'")
    ) {
      const [yearA, monthA] = a.split('-');
      const [yearB, monthB] = b.split('-');
      const yearDiff = parseInt(yearA) - parseInt(yearB);
      if (yearDiff !== 0) return yearDiff;
      return parseInt(monthA) - parseInt(monthB);
    }

    // Fallback to alphabetical sort
    return a.localeCompare(b);
  });
};
